import React, { useState } from "react";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../context/AuthContext";
import { uploadMultiple } from "../utils/cloudinary";
import { CATEGORIES, SUBCATEGORIES_BY_CATEGORY } from "../utils/format";
import { ImagePlus, AlertCircle, CheckCircle } from "lucide-react";
import toast from "react-hot-toast";

export default function BusinessSubmitPage() {
  const { user, profile } = useAuth();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [form, setForm] = useState({
    name: "", category: "", subcategory: "", description: "", price: "", originalPrice: "",
    stock: "", brand: "", specs: "",
  });

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  function handleFiles(e) {
    const selected = Array.from(e.target.files).slice(0, 5);
    setFiles(selected);
    setPreviews(selected.map(f => URL.createObjectURL(f)));
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!profile?.businessApproved && profile?.businessApproved !== undefined) {
      // still allow submission for review
    }
    setLoading(true);
    try {
      let imageUrls = [];
      if (files.length > 0) {
        const uploads = await uploadMultiple(files, "afriport/submissions");
        imageUrls = uploads.map(u => u.url);
      }
      await addDoc(collection(db, "product_submissions"), {
        ...form,
        price: Number(form.price),
        originalPrice: form.originalPrice ? Number(form.originalPrice) : null,
        stock: Number(form.stock) || 0,
        images: imageUrls,
        submittedBy: user.uid,
        businessName: profile?.businessName || "",
        businessEmail: user.email,
        status: "pending",
        createdAt: serverTimestamp(),
      });
      setSubmitted(true);
      toast.success("Product submitted for review!");
    } catch (err) {
      toast.error("Submission failed: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  if (submitted) {
    return (
      <div style={{ minHeight: "80vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
        <div style={{ textAlign: "center", maxWidth: 440 }}>
          <div style={{ width: 72, height: 72, background: "#DCFCE7", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 20px" }}>
            <CheckCircle size={36} color="var(--success)" />
          </div>
          <h2 style={{ fontFamily: "var(--font-head)", fontSize: 26, fontWeight: 700, marginBottom: 12 }}>Submission Received!</h2>
          <p style={{ color: "var(--text-muted)", lineHeight: 1.7, marginBottom: 28 }}>
            Our team will review your product listing within 24–48 hours. You'll be notified by email once it's approved and live on Afriport.
          </p>
          <button onClick={() => setSubmitted(false)} className="btn btn-primary">Submit Another Product</button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: "var(--surface)", minHeight: "100vh" }}>
      <div className="container section-sm" style={{ maxWidth: 680 }}>
        <div style={{ marginBottom: 32 }}>
          <h1 style={{ fontFamily: "var(--font-head)", fontSize: 28, fontWeight: 700 }}>Submit a Product</h1>
          <p style={{ color: "var(--text-muted)", marginTop: 6 }}>Fill out the form below. Afriport's admin team will review and publish your listing.</p>
        </div>

        {/* Commission notice */}
        <div style={{ display: "flex", gap: 12, padding: 16, background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 12, marginBottom: 28 }}>
          <AlertCircle size={18} color="#1D4ED8" style={{ flexShrink: 0, marginTop: 2 }} />
          <p style={{ fontSize: 14, color: "#1E40AF", lineHeight: 1.6 }}>
            <strong>Commission Policy:</strong> Afriport retains <strong>10%</strong> of each sale. The remaining <strong>90%</strong> is automatically deposited to <strong>{profile?.bankName} {profile?.accountNumber}</strong> within 3–5 business days of sale.
          </p>
        </div>

        <div className="card" style={{ padding: 32 }}>
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div className="form-group">
              <label>Product Name *</label>
              <input className="form-input" placeholder="e.g. 200Ah Lithium Iron Phosphate Battery" required value={form.name} onChange={set("name")} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 200px), 1fr))", gap: 16 }}>
              <div className="form-group">
                <label>Category *</label>
                <select
                  className="form-select"
                  required
                  value={form.category}
                  onChange={e => setForm(f => ({ ...f, category: e.target.value, subcategory: "" }))}
                >
                  <option value="">Select category</option>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Sub-Category</label>
                <select
                  className="form-select"
                  value={form.subcategory}
                  onChange={set("subcategory")}
                  disabled={!form.category || !(SUBCATEGORIES_BY_CATEGORY[form.category]?.length)}
                  style={{ opacity: !form.category ? 0.6 : 1 }}
                >
                  <option value="">Select sub-category (optional)</option>
                  {(SUBCATEGORIES_BY_CATEGORY[form.category] || []).map(s => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label>Brand</label>
                <input className="form-input" placeholder="e.g. Dell, Victron" value={form.brand} onChange={set("brand")} />
              </div>
            </div>
            <div className="form-group">
              <label>Description *</label>
              <textarea className="form-input" rows={4} placeholder="Describe the product, features, and what's in the box…" required value={form.description} onChange={set("description")} style={{ resize: "vertical" }} />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))", gap: 16 }}>
              <div className="form-group">
                <label>Selling Price (₦) *</label>
                <input className="form-input" type="number" min={0} placeholder="150000" required value={form.price} onChange={set("price")} />
              </div>
              <div className="form-group">
                <label>Original Price (₦)</label>
                <input className="form-input" type="number" min={0} placeholder="180000" value={form.originalPrice} onChange={set("originalPrice")} />
              </div>
              <div className="form-group">
                <label>Stock Quantity *</label>
                <input className="form-input" type="number" min={0} placeholder="10" required value={form.stock} onChange={set("stock")} />
              </div>
            </div>
            <div className="form-group">
              <label>Key Specs (optional, one per line)</label>
              <textarea className="form-input" rows={3} placeholder={"Capacity: 200Ah\nVoltage: 12V\nWeight: 24kg"} value={form.specs} onChange={set("specs")} style={{ resize: "vertical", fontFamily: "monospace", fontSize: 14 }} />
              <p style={{ fontSize: 12, color: "var(--text-muted)" }}>Format: "Key: Value" on each line</p>
            </div>

            {/* Images */}
            <div className="form-group">
              <label>Product Images (up to 5)</label>
              <label style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 10, padding: 28, border: "2px dashed var(--border)", borderRadius: 12, cursor: "pointer", background: "#fff", transition: "border-color 0.2s" }}
                onDragOver={e => e.preventDefault()} onMouseEnter={e => e.currentTarget.style.borderColor = "var(--brand)"} onMouseLeave={e => e.currentTarget.style.borderColor = "var(--border)"}>
                <input type="file" accept="image/*" multiple onChange={handleFiles} style={{ display: "none" }} />
                <ImagePlus size={28} color="var(--text-muted)" />
                <span style={{ fontSize: 14, color: "var(--text-muted)" }}>Click to upload or drag & drop</span>
                <span style={{ fontSize: 12, color: "var(--text-muted)" }}>PNG, JPG up to 10MB each · Max 5 images</span>
              </label>
              {previews.length > 0 && (
                <div style={{ display: "flex", gap: 10, marginTop: 12, flexWrap: "wrap" }}>
                  {previews.map((src, i) => (
                    <div key={i} style={{ width: 80, height: 80, borderRadius: 8, overflow: "hidden", border: "1px solid var(--border)" }}>
                      <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button className="btn btn-primary btn-lg" type="submit" disabled={loading} style={{ marginTop: 8 }}>
              {loading ? "Uploading & Submitting…" : "Submit for Review"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
